import { useState } from 'react';
import { ShareNetwork as Share2 } from '@phosphor-icons/react';
import ShareModal from './ShareModal';
import { buildSharePreviewUrl } from '../utils/sharePreviewUrl';
import { CtaType, emitCtaClick } from '../services/carEngagementEvents';
import { useShowcaseText } from '../i18n/useShowcaseText';
import './ShareButton.css';

/**
 * Share button on car detail page.
 *
 * Mobile (`navigator.share` available) → opens native share sheet
 * directly. iOS / Android present WhatsApp / Telegram / iMessage / Mail
 * etc. in one tap.
 *
 * Desktop (no `navigator.share`) → opens `ShareModal` with a preview
 * block, copyable URL, and direct-share shortcuts for WhatsApp /
 * Telegram / Email.
 *
 * The shared URL is the b2b-api link-preview endpoint (#909, MR !808)
 * — messenger crawlers fetch its OG/Twitter metadata, so the link
 * renders as a rich card in chats. Plain hash URLs from the address
 * bar (`andys-motors.com/used-cars#/cars/1073`) don't work because the
 * hash never reaches the server.
 *
 * If the car id is missing or both share paths are unavailable
 * (extremely old browsers without `navigator.share` AND without
 * `navigator.clipboard`), the component renders nothing — better than
 * a button that does nothing on click.
 *
 * Props:
 *   - className: string — extra classes for the button itself. The caller
 *     needs it to mark the button print-hidden when it sits inside a block
 *     that IS printed (the title row on the car page).
 *   - car: { id } — required. The id is used to build the share URL.
 *   - title: string — display title for the share sheet / modal.
 *   - imageUrl?: string — preview image inside the modal.
 *   - price?: number — shown next to title in the modal.
 *   - isMobile: boolean — switches between desktop tooltip-icon and
 *     mobile full-width labeled button.
 *   - iconOnly: boolean — keep the round icon even on mobile (no label, no
 *     tooltip); the click still uses the native share sheet there. Used by
 *     the showcase car page (#1335), where share sits next to the title.
 */
const ShareButton = ({
  car,
  title,
  imageUrl,
  price,
  year,
  mileage,
  tagline,
  isMobile = false,
  iconOnly = false,
  className = '',
}) => {
  const showcaseText = useShowcaseText();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const previewUrl = buildSharePreviewUrl(car?.id);
  if (!car?.id || !previewUrl) return null;

  const hasNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const hasClipboard =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function';

  // Nothing useful we can do — hide.
  if (!hasNativeShare && !hasClipboard) return null;

  const handleClick = async () => {
    emitCtaClick(car, CtaType.SHARE);
    if (isMobile && hasNativeShare) {
      try {
        await navigator.share({ title, url: previewUrl });
      } catch (err) {
        // User cancelled the share sheet — normal flow on iOS / Android.
        if (err && err.name !== 'AbortError') {
          console.error('[ShareButton] navigator.share failed:', err);
        }
      }
      return;
    }
    setIsModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={showcaseText('showcase.car.share', 'Share this car')}
        className={`share-button ${
          isMobile && !iconOnly ? 'share-button--mobile' : 'share-button--desktop'
        }${className ? ` ${className}` : ''}`}
      >
        <Share2 className="share-button__icon" />
        {isMobile && !iconOnly && <span className="share-button__label">{showcaseText('showcase.share.label', 'Share')}</span>}
        {!isMobile && !iconOnly && (
          <span className="share-button__tooltip">{showcaseText('showcase.car.share', 'Share this car')}</span>
        )}
      </button>
      {isModalOpen && (
        <ShareModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={title}
          imageUrl={imageUrl}
          previewUrl={previewUrl}
          price={price}
          year={year}
          mileage={mileage}
          tagline={tagline}
        />
      )}
    </>
  );
};

export default ShareButton;
